<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/50" @click="$emit('close')" />

    <!-- Modal -->
    <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-base font-semibold text-slate-800 dark:text-slate-100">
          {{ isEditing ? 'Editar card' : 'Novo card' }}
        </h2>
        <button
          class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <!-- Title -->
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Título <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.title"
            type="text"
            placeholder="Nome do negócio ou oportunidade"
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          />
        </div>

        <!-- Stage selector -->
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Coluna</label>
          <select
            v-model="form.crm_stage_id"
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option v-for="stage in stages" :key="stage.id" :value="stage.id">{{ stage.name }}</option>
          </select>
        </div>

        <!-- Deal value + currency -->
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Valor do negócio</label>
            <input
              v-model.number="form.deal_value"
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div class="w-24">
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Moeda</label>
            <select
              v-model="form.currency"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option value="BRL">BRL</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <!-- Priority + Due date -->
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Prioridade</label>
            <select
              v-model="form.priority"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div class="flex-1">
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Data de vencimento</label>
            <input
              v-model="form.due_date"
              type="date"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
        </div>

        <!-- Assignee -->
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Agente responsável</label>
          <select
            v-model="form.assignee_id"
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option :value="null">Nenhum</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
          </select>
        </div>

        <!-- Conversation search -->
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Vincular conversa</label>

          <!-- Selected conversation badge -->
          <div
            v-if="selectedConversation"
            class="flex items-center justify-between px-3 py-2 text-sm bg-woot-50 dark:bg-woot-900/20 border border-woot-200 dark:border-woot-700 rounded-lg"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span class="i-lucide-message-square size-3.5 text-woot-500 shrink-0" />
              <span class="font-medium text-woot-700 dark:text-woot-300 shrink-0">#{{ selectedConversation.id }}</span>
              <span class="text-slate-600 dark:text-slate-400 truncate text-xs">
                {{ convContactName(selectedConversation) }}
              </span>
            </div>
            <button
              class="ml-2 p-0.5 text-slate-400 hover:text-red-500 rounded transition-colors shrink-0"
              title="Remover vínculo"
              @click="clearConversation"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>

          <!-- Search input -->
          <div v-if="!selectedConversation" class="relative">
            <input
              v-model="convSearch"
              type="text"
              placeholder="Buscar por nome do contato ou #ID..."
              class="w-full pl-3 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @input="onConvSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
            />
            <span
              v-if="isSearching"
              class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-loader-2 size-3.5 text-slate-400 animate-spin pointer-events-none"
            />
            <span
              v-else
              class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-slate-400 pointer-events-none"
            />
          </div>

          <!-- Dropdown results -->
          <div
            v-if="!selectedConversation && showDropdown && convResults.length > 0"
            class="mt-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-h-52 overflow-y-auto"
          >
            <button
              v-for="conv in convResults"
              :key="conv.id"
              class="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
              @mousedown.prevent="selectConversation(conv)"
            >
              <span class="i-lucide-message-square size-4 text-woot-500 shrink-0" />
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-semibold text-woot-600 dark:text-woot-400">#{{ conv.id }}</span>
                  <span class="text-xs text-slate-700 dark:text-slate-300 truncate">
                    {{ convContactName(conv) }}
                  </span>
                </div>
              </div>
              <span
                class="text-xs px-1.5 py-0.5 rounded shrink-0"
                :class="{
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': conv.status === 'open',
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400': conv.status === 'pending',
                  'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400': conv.status === 'resolved',
                }"
              >
                {{ statusLabel(conv.status) }}
              </span>
            </button>
          </div>

          <!-- Empty / hint states -->
          <div
            v-if="!selectedConversation && showDropdown && convSearch.length >= 2 && convResults.length === 0 && !isSearching"
            class="mt-1 px-3 py-3 text-xs text-slate-400 text-center border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
          >
            Nenhuma conversa encontrada para "{{ convSearch }}"
          </div>
          <div
            v-if="!selectedConversation && showDropdown && convSearch.length > 0 && convSearch.length < 2"
            class="mt-1 px-3 py-2 text-xs text-slate-400 text-center border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
          >
            Digite ao menos 2 caracteres para buscar
          </div>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Descrição</label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Detalhes sobre este negócio..."
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 resize-none"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
        <button
          class="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          class="px-4 py-2 text-sm bg-woot-500 hover:bg-woot-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          :disabled="!form.title || isSaving"
          @click="handleSubmit"
        >
          <span v-if="isSaving" class="i-lucide-loader-2 size-3.5 animate-spin" />
          {{ isEditing ? 'Salvar alterações' : 'Criar card' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';

const props = defineProps({
  card: { type: Object, default: null },
  stages: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  defaultStageId: { type: Number, default: null },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);

const isEditing = computed(() => !!props.card?.id);

// ── form state ────────────────────────────────────────────────────────────────
const form = ref({
  title: '',
  crm_stage_id: props.defaultStageId || props.stages[0]?.id || null,
  deal_value: 0,
  currency: 'BRL',
  priority: 'medium',
  due_date: '',
  assignee_id: null,
  conversation_id: null,
  description: '',
});

// ── conversation search ───────────────────────────────────────────────────────
const convSearch = ref('');
const convResults = ref([]);
const selectedConversation = ref(null);
const isSearching = ref(false);
const showDropdown = ref(false);
let searchTimer = null;

// Extracts the contact name from different conversation JSON structures:
// - /api/v1/accounts/:id/conversations  → conv.meta.sender.name
// - /api/v1/accounts/:id/search         → conv.contact.name
function convContactName(conv) {
  if (!conv) return '';
  if (conv.meta?.sender?.name) return conv.meta.sender.name;
  if (conv.contact?.name) return conv.contact.name;
  return 'Sem contato';
}

function statusLabel(status) {
  if (status === 'open') return 'aberta';
  if (status === 'pending') return 'pendente';
  if (status === 'resolved') return 'resolvida';
  return status || '';
}

function getAccountId() {
  const match = window.location.pathname.match(/\/accounts\/(\d+)/);
  return match ? match[1] : null;
}

function onSearchFocus() {
  showDropdown.value = true;
  if (convSearch.value.length >= 2 && convResults.value.length === 0) {
    searchConversations(convSearch.value);
  }
}

function onSearchBlur() {
  setTimeout(() => { showDropdown.value = false; }, 200);
}

function onConvSearchInput() {
  clearTimeout(searchTimer);
  convResults.value = [];
  if (convSearch.value.length < 2) return;
  isSearching.value = true;
  searchTimer = setTimeout(() => searchConversations(convSearch.value), 350);
}

async function searchConversations(q) {
  const accountId = getAccountId();
  if (!accountId) { isSearching.value = false; return; }

  try {
    const numericId = parseInt(q, 10);
    const isNumeric = !isNaN(numericId) && String(numericId) === q.trim();

    if (isNumeric) {
      // Fetch conversation directly by display_id via list endpoint with filter
      const res = await axios.get(`/api/v1/accounts/${accountId}/conversations`, {
        params: { page: 1, assignee_type: 'all' },
      });
      const all = res.data?.data?.payload || [];
      convResults.value = all.filter(c => c.id === numericId).slice(0, 10);
    } else {
      // Use the Chatwoot search endpoint — returns conv.contact.name
      const res = await axios.get(`/api/v1/accounts/${accountId}/search`, {
        params: { q, page: 1 },
      });
      convResults.value = (res.data?.payload?.conversations || []).slice(0, 20);
    }
  } catch {
    // Fallback: list recent conversations and filter client-side
    try {
      const accountId2 = getAccountId();
      const res = await axios.get(`/api/v1/accounts/${accountId2}/conversations`, {
        params: { page: 1, assignee_type: 'all' },
      });
      const all = res.data?.data?.payload || [];
      const lower = q.toLowerCase();
      convResults.value = all
        .filter(c => {
          const name = (c.meta?.sender?.name || '').toLowerCase();
          return String(c.id).includes(q) || name.includes(lower);
        })
        .slice(0, 20);
    } catch {
      convResults.value = [];
    }
  } finally {
    isSearching.value = false;
  }
}

function selectConversation(conv) {
  selectedConversation.value = conv;
  form.value.conversation_id = conv.id;
  showDropdown.value = false;
  convSearch.value = '';
  convResults.value = [];
}

function clearConversation() {
  selectedConversation.value = null;
  form.value.conversation_id = null;
  convSearch.value = '';
  convResults.value = [];
}

// ── watch card prop for edit mode ─────────────────────────────────────────────
watch(() => props.card, (card) => {
  if (card) {
    form.value = {
      title: card.title || '',
      crm_stage_id: card.stage_id || props.defaultStageId || props.stages[0]?.id || null,
      deal_value: card.deal_value || 0,
      currency: card.currency || 'BRL',
      priority: card.priority || 'medium',
      due_date: card.due_date ? card.due_date.split('T')[0] : '',
      assignee_id: card.assignee_id || null,
      conversation_id: card.conversation_id || null,
      description: card.description || '',
    };
    if (card.conversation_id && card.conversation) {
      selectedConversation.value = card.conversation;
    } else {
      selectedConversation.value = null;
    }
  } else {
    form.value = {
      title: '',
      crm_stage_id: props.defaultStageId || props.stages[0]?.id || null,
      deal_value: 0,
      currency: 'BRL',
      priority: 'medium',
      due_date: '',
      assignee_id: null,
      conversation_id: null,
      description: '',
    };
    selectedConversation.value = null;
  }
}, { immediate: true });

// Also update crm_stage_id when defaultStageId changes (e.g. clicking + in a specific column)
watch(() => props.defaultStageId, (id) => {
  if (id && !props.card) {
    form.value.crm_stage_id = id;
  }
});

function handleSubmit() {
  if (!form.value.title) return;
  emit('submit', { ...form.value });
}
</script>
